import moment from "moment";

export const LEAVE_STATUS = {
  APPROVED: "1",
  PENDING: "0",
  REJECTED: "2",
};

export const COMPLIANCE = {
  REJECTED: { key: "rejected", label: "Rejected Leave", css: "lcb lcb--rejected" },
  NO_LEAVE: { key: "no_leave", label: "No Leave", css: "lcb lcb--no-leave" },
  PENDING:  { key: "pending",  label: "Pending",        css: "lcb lcb--pending" },
  APPROVED: { key: "approved", label: "On Leave",       css: "lcb lcb--approved" },
};

/**
 * Fetch leave records for a date range.
 * Returns raw leave records array from /leaveManagement/apply_leave/list.
 */
export const fetchLeaveData = async (baseUrl, token, startDate, endDate, signal) => {
  const params = new URLSearchParams({
    start_date: startDate,
    end_date: endDate || startDate,
    limit: "9999",
    page: "1",
  });
  try {
    const res = await fetch(`${baseUrl}/leaveManagement/apply_leave/list?${params}`, {
      headers: { Authorization: `Bearer ${token}` },
      signal,
    });
    if (!res.ok) return [];
    const json = await res.json();
    return Array.isArray(json.data) ? json.data : Array.isArray(json.result) ? json.result : [];
  } catch (err) {
    if (err.name === "AbortError") throw err;
    return [];
  }
};

/**
 * Build single-date lookup: { [user_id]: { key, label, css, categoryName } }
 * If a user has multiple leave records for the same date, picks highest priority
 * (approved > pending > rejected).
 */
export const buildLeaveMap = (leaveRecords, targetDate) => {
  const map = {};
  const target = moment(targetDate).format("YYYY-MM-DD");
  const priority = { "1": 3, "0": 2, "2": 1 };

  for (const rec of leaveRecords) {
    const userId = rec.user_id;
    const catName = rec.leave_category_name || "Leave";
    const dates = rec.leave_dates_with_status;
    if (!Array.isArray(dates)) continue;

    for (const d of dates) {
      const ld = moment(d.leave_date, ["YYYY-MM-DD", "DD-MM-YYYY"]).format("YYYY-MM-DD");
      if (ld !== target) continue;

      const status = String(d.approval_status);
      const existing = map[userId];
      if (existing && (priority[existing._raw] || 0) >= (priority[status] || 0)) continue;

      const base =
        status === LEAVE_STATUS.APPROVED  ? COMPLIANCE.APPROVED :
        status === LEAVE_STATUS.PENDING   ? COMPLIANCE.PENDING :
        status === LEAVE_STATUS.REJECTED  ? COMPLIANCE.REJECTED : null;

      if (base) map[userId] = { ...base, categoryName: catName, _raw: status };
    }
  }
  return map;
};

/**
 * Get compliance info for one absent staff member.
 * Returns NO_LEAVE if not found in map.
 */
export const getLeaveCompliance = (userId, leaveMap) => {
  return leaveMap[userId] || COMPLIANCE.NO_LEAVE;
};

/**
 * Build multi-date lookup: { [user_id + "_" + YYYY-MM-DD]: compliance }
 * Used by analytics/discipline views spanning multiple days.
 */
export const buildMultiDateLeaveMap = (leaveRecords) => {
  const map = {};
  const priority = { "1": 3, "0": 2, "2": 1 };

  for (const rec of leaveRecords) {
    const userId = rec.user_id;
    const catName = rec.leave_category_name || "Leave";
    const dates = rec.leave_dates_with_status;
    if (!Array.isArray(dates)) continue;

    for (const d of dates) {
      const ld = moment(d.leave_date, ["YYYY-MM-DD", "DD-MM-YYYY"]).format("YYYY-MM-DD");
      const key = `${userId}_${ld}`;
      const status = String(d.approval_status);
      const existing = map[key];
      if (existing && (priority[existing._raw] || 0) >= (priority[status] || 0)) continue;

      const base =
        status === LEAVE_STATUS.APPROVED  ? COMPLIANCE.APPROVED :
        status === LEAVE_STATUS.PENDING   ? COMPLIANCE.PENDING :
        status === LEAVE_STATUS.REJECTED  ? COMPLIANCE.REJECTED : null;

      if (base) map[key] = { ...base, categoryName: catName, _raw: status };
    }
  }
  return map;
};
